import React, { useCallback, useEffect, useState } from 'react';
import { Route, useHistory, useRouteMatch } from 'react-router-dom';
import { Currency, Hours, IconLink, Page, Percent } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { PageLoader } from '~/routes/public/pages';
import {
  BigValue,
  Card,
  DashboardGrid,
  Divider,
  Eyebrow,
  Legend,
  LegendLabel,
  LegendRow,
  LegendShare,
  LegendValue,
  MarginPill,
  ProfitHead,
  shareLabel,
  Unit,
  ValueRow,
} from '../DashboardCards';
import EditBudgetDrawer from './EditBudgetDrawer';

export default function BudgetDashboard({ project }) {
  const { url, path } = useRouteMatch();
  const history = useHistory();
  const api = useApi();
  const { workspace } = useWorkspace();
  const [budget, setBudget] = useState(null);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).budgetDashboard();
    setBudget(data);
  }, [api, workspace.id, project]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!budget) return <PageLoader />;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Budget</Page.Title>

        <Page.Actions>
          <IconLink icon="pencil" tooltip="Edit Budget" to={`${url}/edit`} />
        </Page.Actions>
      </Page.TabHeader>

      {/* The read-only tab is a dashboard of the saved budget; the per-entity ledgers live in the edit drawer. */}
      <BudgetKpiCards budget={budget} isBillable={project.isBillable} />

      <Route path={`${path}/edit`}>
        <EditBudgetDrawer projectId={project.id} onSaved={fetchData} onClose={() => history.push(url)} />
      </Route>
    </>
  );
}

function BudgetKpiCards({ budget, isBillable }) {
  const currency = budget.currency;
  const viewMargin = budget.permissions.viewMargin;
  const money = (value) => <Currency value={value} currency={currency} />;

  const revenueSources = [
    { label: 'Budget Services Revenue', value: budget.servicesRevenue },
    { label: 'Budget Expenses Revenue', value: budget.expensesRevenue },
    { label: 'Budget Products Revenue', value: budget.productRevenue },
    { label: 'Budget Other Items Revenue', value: budget.otherItemsRevenue },
  ];

  // A non-billable project has no revenue, so the revenue-shaped cards (revenue, gross profit, bill rate,
  // realization) come off; hours and cost still mean something.
  return (
    <DashboardGrid>
      {isBillable && (
        <Card>
          <Eyebrow>Budget Revenue</Eyebrow>
          <BigValue>{money(budget.totalRevenue)}</BigValue>
          <Divider />
          <Legend>
            {revenueSources.map((source) => (
              <LegendRow key={source.label}>
                <LegendLabel>{source.label}</LegendLabel>
                <LegendValue>{money(source.value)}</LegendValue>
                <LegendShare>{shareLabel(source.value, budget.totalRevenue)}</LegendShare>
              </LegendRow>
            ))}
          </Legend>
        </Card>
      )}

      <Card>
        <Eyebrow>Budget Hours</Eyebrow>
        <BigValue>
          <Hours value={budget.totalHours} />
        </BigValue>
        <Divider />
        <Legend>
          {isBillable && (
            <LegendRow>
              <LegendLabel>Budget Billable Hours</LegendLabel>
              <LegendValue>
                <Hours value={budget.billableHours} />
              </LegendValue>
              <LegendShare>{shareLabel(budget.billableHours, budget.totalHours)}</LegendShare>
            </LegendRow>
          )}
          <LegendRow>
            <LegendLabel>Budget Non-Billable Hours</LegendLabel>
            <LegendValue>
              <Hours value={budget.nonBillableHours} />
            </LegendValue>
            <LegendShare>{shareLabel(budget.nonBillableHours, budget.totalHours)}</LegendShare>
          </LegendRow>
        </Legend>
      </Card>

      {viewMargin && (
        <Card>
          <Eyebrow>Budget Cost</Eyebrow>
          <BigValue>{money(budget.totalCost)}</BigValue>
          <Divider />
          <Legend>
            <LegendRow>
              <LegendLabel>Budget Services Cost</LegendLabel>
              <LegendValue>{money(budget.servicesCost)}</LegendValue>
              <LegendShare>{shareLabel(budget.servicesCost, budget.totalCost)}</LegendShare>
            </LegendRow>
            <LegendRow>
              <LegendLabel>Budget Expenses Cost</LegendLabel>
              <LegendValue>{money(budget.expensesCost)}</LegendValue>
              <LegendShare>{shareLabel(budget.expensesCost, budget.totalCost)}</LegendShare>
            </LegendRow>
          </Legend>
        </Card>
      )}

      {viewMargin && isBillable && (
        <Card>
          <Eyebrow>Budget Gross Profit</Eyebrow>
          <ProfitHead>
            <BigValue $flush>{money(budget.totalGrossProfit)}</BigValue>
            <MarginPill value={budget.totalGrossMargin} />
          </ProfitHead>
          <Divider />
          <Legend>
            <LegendRow>
              <LegendLabel>Budget Services Gross Profit</LegendLabel>
              <LegendValue>{money(budget.servicesGrossProfit)}</LegendValue>
              <LegendShare>
                <Percent value={budget.servicesGrossMargin} />
              </LegendShare>
            </LegendRow>
          </Legend>
        </Card>
      )}

      {isBillable && (
        <Card>
          <Eyebrow>Budget Effective Bill Rate</Eyebrow>
          <ValueRow>
            <BigValue $flush>{money(budget.effectiveBillRate)}</BigValue>
            <Unit>/ hr</Unit>
          </ValueRow>
          <Divider />
          <Legend>
            <LegendRow>
              <LegendLabel>Budget Services Revenue</LegendLabel>
              <LegendValue>{money(budget.servicesRevenue)}</LegendValue>
            </LegendRow>
            <LegendRow>
              <LegendLabel>Budget Total Hours</LegendLabel>
              <LegendValue>
                <Hours value={budget.totalHours} />
              </LegendValue>
            </LegendRow>
          </Legend>
        </Card>
      )}

      {isBillable && (
        <Card>
          <Eyebrow>Budget Realization Rate</Eyebrow>
          <BigValue>
            <Percent value={budget.realizationRate} />
          </BigValue>
          <Divider />
          <Legend>
            <LegendRow>
              <LegendLabel>Budget Billable Hours</LegendLabel>
              <LegendValue>
                <Hours value={budget.billableHours} />
              </LegendValue>
            </LegendRow>
            <LegendRow>
              <LegendLabel>Budget Total Hours</LegendLabel>
              <LegendValue>
                <Hours value={budget.totalHours} />
              </LegendValue>
            </LegendRow>
          </Legend>
        </Card>
      )}
    </DashboardGrid>
  );
}
